// livePreview.js — RAF-throttled live mil-sym render preview during point collection.
//
// After minPoints are placed, on every pointermove this renders the symbol with
// the collected points + cursor as a provisional final point, writing GeoJSON
// features to LYR_DRAW_PREVIEW at 50% opacity.
//
// Usage:
//   const stop = startLivePreview(map, sidc, getProvisionalCoords);
//   // later:
//   stop();

import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Style, Stroke, Fill } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import { toLonLat } from 'ol/proj';
import { renderTacticalGraphic } from '../renderer/TacticalRenderer';

import { getFillVariant } from '../renderer/fillVariant';

const LYR_DRAW_PREVIEW = 'ol-draw-preview';

const FORMAT = new GeoJSON({ featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326' });

const PREVIEW_STYLE_SOLID = new Style({
    stroke: new Stroke({ color: 'rgba(59,130,246,0.5)', width: 2 }),
    fill: new Fill({ color: 'rgba(59,130,246,0.08)' }),
});

// For CBRN (hatch) — amber dashed outline with no fill, so it's visually distinct
const PREVIEW_STYLE_HATCH = new Style({
    stroke: new Stroke({ color: 'rgba(234,179,8,0.7)', width: 2, lineDash: [6, 3] }),
    fill: new Fill({ color: 'rgba(234,179,8,0.04)' }),
});

// For minefield (perimeter-stamp) — red-toned dashed outline with no fill
const PREVIEW_STYLE_PERIMETER = new Style({
    stroke: new Stroke({ color: 'rgba(239,68,68,0.7)', width: 2, lineDash: [4, 4] }),
    fill: new Fill({ color: 'rgba(239,68,68,0.04)' }),
});

function previewStyleForSidc(sidc) {
    if (!sidc) return PREVIEW_STYLE_SOLID;
    const v = getFillVariant(sidc);
    if (v === 'hatch') return PREVIEW_STYLE_HATCH;
    if (v === 'perimeter-stamp') return PREVIEW_STYLE_PERIMETER;
    return PREVIEW_STYLE_SOLID;
}

export function ensurePreviewLayer(map, sidc) {
    let layer = map.getLayers().getArray().find((l) => l.get('id') === LYR_DRAW_PREVIEW);
    if (!layer) {
        const src = new VectorSource();
        layer = new VectorLayer({ source: src, style: () => previewStyleForSidc(layer.get('_previewSidc')), zIndex: 9, opacity: 0.5 });
        layer.set('id', LYR_DRAW_PREVIEW);
        map.addLayer(layer);
    }
    if (sidc !== undefined) layer.set('_previewSidc', sidc);
    return layer;
}

export function clearPreviewLayer(map) {
    const layer = map.getLayers().getArray().find((l) => l.get('id') === LYR_DRAW_PREVIEW);
    layer?.getSource().clear(true);
}

/**
 * Start live preview for the given SIDC.
 *
 * @param {import('ol/Map').default} map
 * @param {string} sidc
 * @param {() => [number,number][]} getCollected — returns current collected [lng,lat] pairs
 * @param {number} minPoints — don't preview until at least this many collected
 * @param {object} [options]
 * @param {boolean|(() => boolean)} [options.cursorFirst=false] — prepend the live cursor
 *   before `getCollected()`'s points instead of appending it after (may be a function,
 *   re-evaluated every frame, for collectors whose correct cursor position changes with
 *   internal phase — see makeAxisCollector in miscBespokeTools.js). Needed by reverseOnFinish
 *   direction rules (see directionalArrowTools.js) where the cursor represents the user's
 *   tentative P1 (tip) and the already-placed click(s) become the trailing rear point(s) —
 *   the opposite of the default "collected so far, then cursor as the provisional final
 *   point" order.
 * @returns {() => void} stop function — call on finish or destroy
 */
export function startLivePreview(map, sidc, getCollected, minPoints = 1, options = {}) {
    if (!sidc || !map) return () => {};
    const { cursorFirst = false } = options;

    const layer = ensurePreviewLayer(map, sidc);
    const source = layer.getSource();
    let rafId = null;
    let lastCursor = null;

    const render = () => {
        const collected = getCollected();
        if (!lastCursor || collected.length < minPoints) { source.clear(true); return; }
        const cursorLonLat = toLonLat(lastCursor);
        const first = typeof cursorFirst === 'function' ? cursorFirst() : cursorFirst;
        const provisional = first ? [cursorLonLat, ...collected] : [...collected, cursorLonLat];
        const geojson = renderTacticalGraphic(sidc, provisional);
        source.clear(true);
        if (!geojson?.features) return;
        geojson.features.forEach((f, i) => {
            try {
                const olF = FORMAT.readFeature(f);
                olF.setId(`preview-${i}`);
                source.addFeature(olF);
            } catch (_) {}
        });
    };

    const onPointerMove = (e) => {
        lastCursor = e.coordinate;
        if (rafId) return; // already scheduled
        rafId = requestAnimationFrame(() => { rafId = null; render(); });
    };

    map.on('pointermove', onPointerMove);

    return function stop() {
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        map.un('pointermove', onPointerMove);
        source.clear(true);
    };
}
